import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import EmptyState from './EmptyState';

/**
 * Envoltura de estados de una query de react-query: carga → error (con reintento)
 * → vacío → datos. Hasta ahora ninguna página manejaba isError, así que un fallo
 * de carga dejaba la pantalla girando o con un vacío indistinguible de "sin datos".
 * Esto da el patrón limpio: el render-prop `children(data)` SOLO corre cuando hay
 * datos (no hace falta guardar undefined a mano).
 *
 * Uso:
 *   <QueryState query={useFoo()} emptyTitle="Sin elementos">
 *     {(data) => <Lista items={data} />}
 *   </QueryState>
 */
interface QueryStateProps<T> {
  query: Pick<UseQueryResult<T>, 'data' | 'isLoading' | 'isError' | 'refetch'>;
  children: (data: T) => ReactNode;
  /** Override del esqueleto de carga. */
  loading?: ReactNode;
  /** Título del estado de error (default genérico). */
  errorTitle?: string;
  /** ¿Los datos cuentan como vacíos? Default: array vacío o null/undefined. */
  isEmpty?: (data: T) => boolean;
  /** Override del estado vacío. */
  empty?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}

const defaultIsEmpty = (data: unknown): boolean =>
  data == null || (Array.isArray(data) && data.length === 0);

/**
 * Estado de error suelto con reintento, para páginas con mucha lógica derivada
 * antes del return donde el render-prop de QueryState resultaría invasivo. Uso:
 *   if (q.isError) return <QueryError onRetry={q.refetch} />;
 */
export function QueryError({ title, onRetry }: { title?: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon={<AlertTriangle size={28} className="text-red-600 dark:text-red-400" />}
      title={title ?? 'No se pudieron cargar los datos'}
      description="Ha fallado la carga. Revisa tu conexión y reintenta."
      action={
        onRetry && (
          <Button size="sm" variant="outline" className="gap-1" onClick={() => onRetry()}>
            <RefreshCw size={14} /> Reintentar
          </Button>
        )
      }
    />
  );
}

export default function QueryState<T>({
  query, children, loading, errorTitle, isEmpty, empty,
  emptyTitle, emptyDescription, emptyAction,
}: QueryStateProps<T>) {
  if (query.isLoading) {
    return (loading as ReactNode) ?? (
      <div className="space-y-3 py-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError || query.data === undefined) {
    return (
      <EmptyState
        icon={<AlertTriangle size={28} className="text-red-600 dark:text-red-400" />}
        title={errorTitle ?? 'No se pudieron cargar los datos'}
        description="Ha fallado la carga. Revisa tu conexión y reintenta."
        action={
          <Button size="sm" variant="outline" className="gap-1" onClick={() => query.refetch?.()}>
            <RefreshCw size={14} /> Reintentar
          </Button>
        }
      />
    );
  }

  const data = query.data as T;
  const empties = isEmpty ?? defaultIsEmpty;
  if (empties(data)) {
    return (empty as ReactNode) ?? (
      <EmptyState title={emptyTitle ?? 'Nada por aquí todavía'} description={emptyDescription} action={emptyAction} />
    );
  }

  return <>{children(data)}</>;
}
